import {
  Button,
  cn,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  useUncontrolledState,
  withKey,
} from "@tellescope/ui";
import { hashString, MaybeAsync } from "@tellescope/utils";
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { EventMetadataRow, EventMetadataRowProps } from "./EventMetadataRow";
import { useTranslation } from "react-i18next";
import { usePermissionQuery } from "@tellescope/tanstack-query";

export interface EventMetadataInputRowProps extends Omit<EventMetadataRowProps, "children"> {
  value: string;
  onSave: MaybeAsync<(newVal: string) => void>;
  isEditing?: boolean;
  setIsEditing?: Dispatch<SetStateAction<boolean>>;
  placeholder?: string;
  textarea?: boolean;
  suggestions?: string[];
  eventId: string;
}

export const EventMetadataInputRow = withKey(
  ({
    value,
    onSave,
    isEditing: providedIsEditing,
    setIsEditing: providedSetIsEditing,
    placeholder,
    textarea = false,
    suggestions,
    eventId,
    ...rest
  }: EventMetadataInputRowProps) => {
    const { t } = useTranslation("calendar");
    const [isEditing, setIsEditing] = useUncontrolledState({
      state: providedIsEditing ?? false,
      setState: providedSetIsEditing,
    });
    const {
      update: { access: canEdit },
    } = usePermissionQuery({
      model: "calendar_events",
      actions: ["update"],
      recordId: eventId,
    });
    const [isSaving, setIsSaving] = useState(false);
    const [newVal, setNewVal] = useState(value);
    const [popoverIsOpen, setPopoverIsOpen] = useState(false);

    const showPlaceholder = !value && !isEditing;
    const useCombobox = !textarea && !!suggestions?.length;

    const filteredSuggestions = useMemo(() => {
      if (!suggestions?.length) return [];
      const lower = newVal.toLowerCase();
      return suggestions.filter((s) => s.toLowerCase().includes(lower));
    }, [suggestions, newVal]);

    const InputComponent = textarea ? Textarea : Input;

    const handleSave = useCallback(
      async (val?: string) => {
        setIsSaving(true);
        try {
          await onSave(val ?? newVal);
        } finally {
          setIsSaving(false);
        }
      },
      [newVal, onSave]
    );

    if (!canEdit) {
      return <EventMetadataRow {...rest}>{value}</EventMetadataRow>;
    }

    return (
      <EventMetadataRow {...rest} fadedText={!value && !isEditing}>
        {isEditing ? (
          <div className="flex flex-col gap-2 justify-start">
            {useCombobox ? (
              <Popover open={popoverIsOpen && filteredSuggestions.length > 0} onOpenChange={setPopoverIsOpen}>
                <PopoverTrigger asChild>
                  <Input
                    onFocus={() => setPopoverIsOpen(true)}
                    onClick={() => setPopoverIsOpen(true)}
                    value={newVal}
                    onChange={(e) => {
                      setNewVal(e.target.value);
                      setPopoverIsOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSave();
                      }
                    }}
                    disabled={isSaving}
                    placeholder={placeholder}
                    autoFocus
                    type="input"
                  />
                </PopoverTrigger>
                <PopoverContent
                  className="flex flex-col gap-0 p-1"
                  align="start"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  {filteredSuggestions.map((suggestion) => (
                    <Button
                      key={suggestion}
                      onClick={() => {
                        setNewVal(suggestion);
                        handleSave(suggestion);
                        setPopoverIsOpen(false);
                      }}
                      variant="ghost"
                      className="text-sm font-light p-1 justify-start w-full h-fit"
                    >
                      {suggestion}
                    </Button>
                  ))}
                </PopoverContent>
              </Popover>
            ) : (
              <InputComponent
                value={newVal}
                onChange={(e) => setNewVal(e.target.value)}
                disabled={isSaving}
                placeholder={placeholder}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    handleSave();
                  }
                }}
                autoFocus
                style={
                  textarea
                    ? {
                        fieldSizing: "content",
                      }
                    : undefined
                }
              />
            )}
            <div className="flex gap-2">
              <Button onClick={() => handleSave()} loading={isSaving}>
                {t("Save")}
              </Button>
              <Button variant="ghost" disabled={isSaving} onClick={() => setIsEditing(false)}>
                {t("Cancel")}
              </Button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className={cn(
              "flex justify-start p-0 m-0 text-sm font-light text-left text-muted-foreground",
              showPlaceholder && "italic"
            )}
          >
            {value || placeholder}
          </button>
        )}
      </EventMetadataRow>
    );
  },
  (props) => `${props.hoverLabel ?? "metadata-row"}-${hashString(props.value)}`
);
